import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useAnimationControls, useMotionValue } from "framer-motion";
import { LEARN } from "../../theme";
import { ConfettiBurst } from "../../feedback/Celebration";
import { learnAssetUrl } from "../../media/learnAssets";
import { itemKey, seededShuffle, useCompleteOnce } from "./engineUtils";
import { Art, Counter, ROOT_STYLE, SparkleBurst, useArenaMetrics, useLetterAccent } from "./tapUtils";

const BASKET_ART = "counting/activities/counting_piece_basket";

/**
 * Word pictures fall through the arena. Slide the basket under the
 * target-letter words (pointer follow) or tap them straight out of the air.
 * Catching a distractor counts a wrong; letting one fall is fine.
 * Completes after `catchCount` correct catches.
 */
export default function CatchFallingGame({ letter, config = {}, speak, sfx, onComplete, onWrong, reduceMotion }) {
  const finish = useCompleteOnce(onComplete);
  const { accent, deep } = useLetterAccent(letter);
  const need = config.catchCount || 3;
  const arenaRef = useRef(null);
  const { w, h, tile } = useArenaMetrics(arenaRef);

  const pool = useMemo(
    () => ({ t: seededShuffle(config.targetItems || [], 3), d: seededShuffle(config.distractorItems || [], 9) }),
    [config.targetItems, config.distractorItems],
  );

  const [items, setItems] = useState([]);
  const itemsRef = useRef([]);
  const [caught, setCaught] = useState([]); // caught target items (art in the basket)
  const [sparkle, setSparkle] = useState(0);
  const [basketArtFailed, setBasketArtFailed] = useState(false);
  const spawnCount = useRef(0);
  const lastLane = useRef(-1);
  const resolved = useRef(new Set());
  const done = caught.length >= need;

  // One falling piece is 1.2 sticker tiles (`--tile`, never under 64px) so
  // the art reads from across the room; the basket is ~2.4 tiles wide. If
  // the stage variable can't be read yet, derive the same size the stage
  // would from the arena box.
  const stageTile = w && h ? Math.max(56, Math.min(112, Math.floor(Math.min(w / 6.5, h / 6)))) : 72;
  const itemPx = Math.max(64, Math.round((tile || stageTile) * 1.2));
  const basketW = itemPx * 2.4;
  const basketH = basketW * 0.7;
  const basketX = useMotionValue(0);
  const basket = useAnimationControls();
  const fallMs = reduceMotion ? 5200 : 3400;

  useEffect(() => { itemsRef.current = items; }, [items]);

  // Centre the basket once the arena is measured.
  useEffect(() => { if (w) basketX.set(Math.max(0, (w - basketW) / 2)); }, [w, basketW, basketX]);

  useEffect(() => {
    if (!done) return undefined;
    sfx?.("sparkle");
    const t = setTimeout(finish, reduceMotion ? 250 : 1000);
    return () => clearTimeout(t);
  }, [done, finish, reduceMotion, sfx]);

  // Spawner.
  useEffect(() => {
    if (done || !w || !h) return undefined;
    const lanes = 5;
    const spawn = () => {
      if (itemsRef.current.length >= 5) return;
      spawnCount.current += 1;
      const n = spawnCount.current;
      const useTarget = pool.t.length && (!pool.d.length || (n * 7919) % 100 < 55);
      const src = useTarget ? pool.t : pool.d;
      if (!src.length) return;
      const item = src[n % src.length];
      let lane = (n * 3 + Math.floor(Math.random() * lanes)) % lanes;
      if (lane === lastLane.current) lane = (lane + 2) % lanes;
      lastLane.current = lane;
      const inset = itemPx * 0.35;
      const usable = Math.max(0, w - itemPx - inset * 2);
      const x = inset + (usable * lane) / (lanes - 1) + (Math.random() - 0.5) * Math.min(24, usable / lanes / 2);
      const entry = { id: `${itemKey(item)}-${n}`, item, isTarget: !!useTarget && item.isTarget !== false, x: Math.max(inset, Math.min(inset + usable, x)) };
      setItems((a) => [...a, entry]);
    };
    spawn();
    const iv = setInterval(spawn, reduceMotion ? 1900 : 1350);
    return () => clearInterval(iv);
  }, [pool, w, h, itemPx, done, reduceMotion]);

  const remove = useCallback((id) => setItems((a) => a.filter((x) => x.id !== id)), []);

  const resolve = useCallback(
    (entry) => {
      if (done || resolved.current.has(entry.id) || !itemsRef.current.some((x) => x.id === entry.id)) return;
      resolved.current.add(entry.id);
      remove(entry.id);
      if (entry.isTarget) {
        sfx?.("snap");
        speak?.(entry.item.word);
        setCaught((c) => [...c, entry.item]);
        setSparkle((s) => s + 1);
        if (!reduceMotion) basket.start({ scale: [1, 1.12, 1], rotate: [0, -4, 4, 0], transition: { duration: 0.42 } });
      } else {
        onWrong?.();
        if (!reduceMotion) basket.start({ x: [0, -10, 10, -6, 6, 0], transition: { duration: 0.42 } });
      }
    },
    [done, remove, sfx, speak, reduceMotion, basket, onWrong],
  );

  // Collision check driven by each item's own fall animation.
  const checkCatch = useCallback(
    (entry, top) => {
      if (!h) return;
      const bTop = h - 6 - basketH;
      const itemBottom = top + itemPx;
      if (itemBottom < bTop + basketH * 0.3 || top > bTop + basketH * 0.55) return;
      const bx = basketX.get();
      const cx = entry.x + itemPx / 2;
      if (cx < bx + basketW * 0.06 || cx > bx + basketW * 0.94) return;
      resolve(entry);
    },
    [h, basketH, itemPx, basketX, basketW, resolve],
  );

  const moveBasket = useCallback((e) => {
    const r = arenaRef.current?.getBoundingClientRect();
    if (!r || !w) return;
    // The basket may hang ~40% past either wall so its mouth can sit under a piece at the edge.
    const overhang = basketW * 0.4;
    basketX.set(Math.max(-overhang, Math.min(w - basketW + overhang, e.clientX - r.left - basketW / 2)));
  }, [w, basketW, basketX]);

  // Keep following the pointer even when it slips over the rails beside the arena.
  useEffect(() => {
    const onMove = (e) => { if (e.pointerType === "mouse" || e.buttons) moveBasket(e); };
    window.addEventListener("pointermove", onMove);
    return () => window.removeEventListener("pointermove", onMove);
  }, [moveBasket]);

  return (
    <div style={ROOT_STYLE}>
      <Counter done={caught.length} total={need} accent={accent} suffix="caught" />
      <div
        ref={arenaRef}
        onPointerDown={moveBasket}
        style={{ position: "absolute", inset: 0, overflow: "hidden", borderRadius: LEARN.radius.lg, touchAction: "none" }}
      >
        <ConfettiBurst active={done} originY={70} count={36} reduceMotion={reduceMotion} />
        <AnimatePresence>
          {h > 0 && items.map((entry) => (
            <motion.div
              key={entry.id}
              initial={{ top: -itemPx * 1.2 }}
              animate={{ top: h }}
              exit={{ opacity: 0, scale: 0.4, transition: { duration: reduceMotion ? 0 : 0.18 } }}
              transition={{ duration: fallMs / 1000, ease: "linear" }}
              onUpdate={(latest) => checkCatch(entry, typeof latest.top === "number" ? latest.top : parseFloat(latest.top))}
              onAnimationComplete={() => remove(entry.id)}
              style={{ position: "absolute", left: entry.x, width: itemPx, height: itemPx }}
            >
              <motion.button
                type="button"
                aria-label={entry.item.word}
                onClick={() => resolve(entry)}
                animate={reduceMotion ? { rotate: 0 } : { rotate: [-6, 6, -6] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                whileTap={reduceMotion ? undefined : { scale: 0.92 }}
                style={{
                  width: "100%", height: "100%", borderRadius: "26%", padding: 0, cursor: "pointer",
                  background: "#fff", border: `2px solid ${LEARN.color.line}`, boxShadow: LEARN.shadow.card,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  WebkitTapHighlightColor: "transparent", touchAction: "none", outline: "none",
                }}
              >
                <Art letter={letter} assetName={entry.item.assetName} word={entry.item.word} alt="" />
              </motion.button>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Basket */}
        <motion.div
          aria-label="Basket"
          role="img"
          style={{ position: "absolute", bottom: 6, left: 0, x: basketX, width: basketW, height: basketH, pointerEvents: "none", zIndex: 3 }}
        >
        <motion.div animate={basket} style={{ position: "absolute", inset: 0 }}>
          <SparkleBurst trigger={sparkle} color={accent} reduceMotion={reduceMotion} distance={basketW * 0.45} />
          <div style={{ position: "absolute", left: "14%", right: "14%", top: "-2%", height: "34%", display: "flex", justifyContent: "center", alignItems: "flex-end", gap: 2, zIndex: 2 }}>
            {caught.slice(-4).map((it, i) => (
              <motion.span
                key={`${itemKey(it)}-${i}`}
                initial={reduceMotion ? false : { y: -20, scale: 0.6, opacity: 0 }}
                animate={{ y: 0, scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 380, damping: 22 }}
                style={{ width: `${Math.min(34, 68 / Math.min(caught.length, 4))}%`, aspectRatio: "1 / 1", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <Art letter={letter} assetName={it.assetName} word={it.word} alt="" style={{ width: "100%", height: "100%" }} />
              </motion.span>
            ))}
          </div>
          {!basketArtFailed ? (
            <img
              src={learnAssetUrl(BASKET_ART)}
              alt=""
              aria-hidden="true"
              draggable={false}
              onError={() => setBasketArtFailed(true)}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", filter: "drop-shadow(0 10px 16px rgba(11,26,51,0.25))", zIndex: 3 }}
            />
          ) : (
            <span aria-hidden="true" style={{ position: "absolute", left: "6%", right: "6%", bottom: "4%", height: "58%", borderRadius: "18% 18% 30% 30% / 30% 30% 40% 40%", background: `linear-gradient(180deg, ${LEARN.color.sun} 0%, ${LEARN.color.sunDeep} 100%)`, boxShadow: LEARN.shadow.sticker(deep), zIndex: 3 }} />
          )}
        </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
